/**
 * Site-wide cookie consent — state + React context.
 *
 * What it gates:
 *   - Hotjar (in-page feedback widget + heatmaps; session recording is
 *     also disabled on the calculator route via Hotjar dashboard rules,
 *     see Item G in the calculator backlog) — fully gated: only loads on
 *     accept.
 *   - Google tag / gtag.js (Google Ads conversion measurement) — runs in
 *     **Consent Mode v2**: the tag loads on every visit but boots with all
 *     consent signals denied (no advertising cookies), and we upgrade them
 *     to granted only after the visitor accepts. So here we gate the
 *     *cookies/signals*, not whether the tag loads. See `shared/gtag.ts`.
 *
 * What it does NOT currently gate (pilot-time exception):
 *   - Mixpanel — initialised unconditionally from `App.tsx` during the
 *     controlled pilot, under legitimate interest. Must be re-gated
 *     before the public launch of Jody. Tracked as Item H in the
 *     calculator backlog.
 *
 * What it does NOT gate:
 *   - The consent decision itself (we set one localStorage key, which
 *     is strictly necessary under the ePrivacy "strictly necessary"
 *     exception and therefore does not itself require consent).
 *   - Service-essential storage like auth tokens or feature flags.
 *
 * Legal frame: ePrivacy Directive (cookie consent) + GDPR. "Reject"
 * must be exactly as easy as "Accept" — keep the banner UI symmetric.
 *
 * Storage key is `jody_consent` per the product naming rule in
 * CLAUDE.md ("the product is Jody, the repo is jod.ai").
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  readConsentCookie,
  writeConsentCookie,
  clearConsentCookie,
} from './consentCookie';

// Legacy localStorage key (pre-cookie). Read only, for one-time migration
// to the shared `.jody.travel` cookie — see readStored / writeStored.
const STORAGE_KEY = 'jody_consent';

export type ConsentState = 'unset' | 'accepted' | 'rejected';

interface ConsentContextValue {
  consent: ConsentState;
  accept: () => void;
  reject: () => void;
  /** Clears the stored choice so the banner re-appears. Wire to a footer
   *  "Manage privacy" link if/when we add one. */
  reset: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

function readStored(): ConsentState {
  if (typeof window === 'undefined') return 'unset';
  try {
    const cookie = readConsentCookie();
    if (cookie) return cookie;

    // One-time migration: earlier builds stored the decision in
    // localStorage (per-origin). If we find one but no cookie yet, adopt
    // it and promote it to the shared `.jody.travel` cookie so the choice
    // now carries across subdomains and the user isn't re-prompted.
    const legacy = window.localStorage.getItem(STORAGE_KEY);
    if (legacy === 'accepted' || legacy === 'rejected') {
      writeConsentCookie(legacy);
      return legacy;
    }
    return 'unset';
  } catch {
    return 'unset';
  }
}

function writeStored(value: ConsentState) {
  if (typeof window === 'undefined') return;
  try {
    if (value === 'unset') {
      clearConsentCookie();
      // Also drop any legacy localStorage value so a reset fully clears.
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* no-op */ }
    } else {
      writeConsentCookie(value);
    }
  } catch {
    // Cookies may be unavailable (Safari private mode, etc.); the consent
    // state still lives in React memory for this session, which is the
    // GDPR-safe fallback (we just don't remember the choice across
    // reloads — the user will see the banner again).
  }
}

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentState>(() => readStored());

  // Hotjar — fully gated. Lazy-loaded only when consent is granted, so a
  // "reject" visit never even downloads its init code. Idempotent on
  // Hotjar's side; safe across re-renders.
  //
  // Mixpanel is **not** gated here during the controlled pilot — see
  // the file header. App.tsx initialises it unconditionally.
  useEffect(() => {
    if (consent !== 'accepted') return;
    void import('../tracking').then((m) => {
      if (typeof m.initHotjar === 'function') m.initHotjar();
    });
  }, [consent]);

  // Google Ads tag — Consent Mode v2. Load the tag on every visit (so
  // Google can detect it and model conversions) but with consent denied by
  // default inside initGtag; then push the visitor's actual choice. We
  // leave the denied default in place while the choice is still 'unset'.
  // initGtag is idempotent, so re-running on each consent change is safe.
  useEffect(() => {
    void import('../gtag').then((m) => {
      m.initGtag();
      if (consent === 'accepted') m.setGtagConsent(true);
      else if (consent === 'rejected') m.setGtagConsent(false);
    });
  }, [consent]);

  const accept = useCallback(() => {
    writeStored('accepted');
    setConsent('accepted');
  }, []);

  const reject = useCallback(() => {
    writeStored('rejected');
    setConsent('rejected');
  }, []);

  const reset = useCallback(() => {
    writeStored('unset');
    setConsent('unset');
  }, []);

  const value = useMemo(
    () => ({ consent, accept, reject, reset }),
    [consent, accept, reject, reset]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used inside <ConsentProvider>');
  }
  return ctx;
}
